import { Component } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { puedeVerIA } from 'src/app/services/ia/ia-formato';

interface MulticomercioOption {
  title: string;
  description: string;
  icon: string;
  route: string;
  color: string;
  // La misma regla con la que cada entrada se mostraba suelta en el menú (antes del 21-oct).
  visible: (auth: AuthService) => boolean;
}

/**
 * Panel de Multicomercio (21-oct, pedido de Reybier): las nueve entradas de multicomercio
 * que estaban sueltas arriba de Contactos ahora cuelgan de un solo ítem del menú, como Ventas.
 * Cada recuadro pide su propio permiso de Multicomercio en la matriz. Altas y Workflows
 * pedían «algo de Parámetros del Sistema» (config) y se colaban con Multicomercio denegado:
 * ahora piden la fila «Altas de Comercios y Workflows» (config.workflows.*).
 */
@Component({
  selector: 'app-multicomercio-dashboard',
  templateUrl: './multicomercio-dashboard.component.html',
  styleUrls: []
})
export class MulticomercioDashboardComponent {
  static readonly OPCIONES: MulticomercioOption[] = [
    {
      title: 'Organización',
      description: 'La estructura de CrediTotal: empresas, comercios y su relación',
      icon: 'fas fa-building',
      route: '/console/organizacion',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.organizacion.read']) : a.IsPermitid(['OWNER', 'ADMIN'])
    },
    {
      title: 'Comercios',
      description: 'La ficha de cada comercio: tiendas, cuentas, condiciones, usuarios y cierre de caja',
      icon: 'fas fa-handshake',
      route: '/console/merchants',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.comercios.read']) : a.IsPermitid(['OWNER', 'ADMIN'])
    },
    {
      title: 'Liquidación',
      description: 'Lo que CrediTotal le paga a cada comercio: preparar, aprobar y pagar',
      icon: 'fas fa-hand-holding-usd',
      route: '/console/liquidacion',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() && a.hasModuleAccess('liquidacion')
    },
    {
      title: 'Solicitudes de crédito',
      description: 'El rastro de cada solicitud de crédito que llega por la caja de un comercio',
      icon: 'fas fa-file-invoice-dollar',
      route: '/console/solicitudes-credito',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.solicitudes_credito.read']) : a.IsPermitid(['OWNER', 'ADMIN', 'ANALIST'])
    },
    {
      title: 'Altas de comercios',
      description: 'Los comercios que quieren entrar a CrediTotal y en qué etapa va cada uno',
      icon: 'fas fa-store-alt',
      route: '/console/altas',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.workflows.read', 'config.workflows.update']) : a.IsPermitid(['OWNER', 'ADMIN', 'ANALIST'])
    },
    {
      title: 'Workflows',
      description: 'Qué perfiles aprueban cada tipo de solicitud y en qué orden',
      icon: 'fas fa-route',
      route: '/console/workflows',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.workflows.read', 'config.workflows.update']) : a.IsPermitid(['OWNER', 'ADMIN', 'ANALIST'])
    },
    {
      title: 'Tipos y pasos del cliente',
      description: 'Los tipos de conexión de la caja y los pasos que le pide al cliente cada uno',
      icon: 'fas fa-sitemap',
      route: '/console/tipos-flujos',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.tipos_flujos.read']) : a.IsPermitid(['OWNER', 'ADMIN', 'ANALIST'])
    },
    {
      title: 'Documentación de integración',
      description: 'Los manuales y la colección de Postman para el comercio que se va a integrar',
      icon: 'fas fa-book',
      route: '/console/documentacion-integracion',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => a.hasRbac$.getValue() ? a.hasAnyPermission(['config.documentacion_integracion.read']) : a.IsPermitid(['OWNER', 'ADMIN', 'ANALIST'])
    },
    {
      title: 'Inteligencia artificial',
      description: 'Las claves, los modelos, el gasto y el asistente de cada comercio',
      icon: 'fas fa-robot',
      route: '/console/ia',
      color: 'bg-blue-100 text-blue-600',
      visible: (a) => puedeVerIA(a)
    }
  ];

  /** ¿Ve al menos un recuadro? Es la regla del ítem «Multicomercio» en el menú. */
  static algunaVisible(auth: AuthService): boolean {
    return MulticomercioDashboardComponent.OPCIONES.some((o) => o.visible(auth));
  }

  constructor(public _auth: AuthService) {}

  get opciones(): MulticomercioOption[] {
    return MulticomercioDashboardComponent.OPCIONES.filter((o) => o.visible(this._auth));
  }
}
